// The sign-in round trip through the identity server. Login is a full-page
// redirect: the identity server sends the person on to the hosted login as this
// app, and the return trip lands on the app origin with `?code=` — a one-time
// code only this browser's key can redeem — and is completed here.
import { authorizeUrl, exchangeCode, logoutUrl, relayCallback } from './identityServerClient.js';
import { createPkcePair } from './pkce.js';
import { useIdentityStorage, useSignInSheet } from '../adapters/ports.js';

const STATE_KEY_PREFIX = 'aac.auth.state.';
// Set while the one automatic "sign in again, by hand" is under way, so a second
// `login_required` in a row is shown to the person, not looped on. It lives in
// localStorage for the reason the state record does: iOS Safari drops
// sessionStorage on the Google round trip. It outlives the tab, so a retry the
// person abandons leaves it set; the next `login_required` is then shown rather
// than retried, and that clears it.
const FRESH_LOGIN_KEY = 'aac.auth.fresh-login.v1';

function freshLoginUnderWay() {
  try {
    return Boolean(globalThis.localStorage.getItem(FRESH_LOGIN_KEY));
  } catch {
    return false;
  }
}

// Whether the marker is now what was asked for. A store that refuses answers
// false, and the caller then reports the refusal instead of retrying, so a
// browser that will not keep the marker cannot loop.
function markFreshLogin(underWay) {
  try {
    if (underWay) {
      globalThis.localStorage.setItem(FRESH_LOGIN_KEY, '1');
    } else {
      globalThis.localStorage.removeItem(FRESH_LOGIN_KEY);
    }
    return true;
  } catch {
    return false;
  }
}

// The record lives in localStorage on this origin, and only there. sessionStorage
// is not used: iOS Safari drops it when the Google round trip comes back through
// another site, and localStorage survives that. Each record is keyed by its
// sign-in's own state, so two tabs signing in at once do not collide. A return is
// accepted only when its state is one of these records.
function stateStores() {
  return globalThis.localStorage ? [globalThis.localStorage] : [];
}

// Inside the native app the WebView drops BOTH web stores across the top-level
// hop to the identity server and back, the way iOS Safari drops sessionStorage.
// The native preferences store the portal already keeps the session in survives
// it. This lib cannot import the portal, so it asks for that same instance
// through its own storage seam, which the host fills at boot.
//
// Only the native shell gets this. A Capacitor whose platform is web is a
// browser — its Preferences IS localStorage — and on the browser path the
// seam's in-memory default would answer for a record no navigation could have
// carried, hiding a real mismatch.
function nativeStateStore() {
  const capacitor = globalThis.Capacitor;
  if (!capacitor) return null;
  const isNative = typeof capacitor.isNativePlatform === 'function'
    ? capacitor.isNativePlatform()
    : typeof capacitor.getPlatform === 'function' && capacitor.getPlatform() !== 'web';
  return isNative ? useIdentityStorage() : null;
}

async function rememberState(key, value) {
  const json = JSON.stringify(value);
  for (const store of stateStores()) {
    try {
      store.setItem(key, json);
    } catch {
      // A store can refuse the write (an old Safari private window). The sign-in then
      // fails its return check, which is the safe answer.
    }
  }
  // Awaited by the caller before it navigates: the navigation tears down
  // everything in memory, so an unfinished native write never lands.
  await nativeStateStore()?.set(key, json);
}

async function forgetState(key) {
  for (const store of stateStores()) {
    try {
      store.removeItem(key);
    } catch {
      // Already gone, or the store is blocked.
    }
  }
  await nativeStateStore()?.remove(key);
}

// Whether a record for this exact state exists anywhere, without taking it.
async function hasState(key) {
  for (const store of stateStores()) {
    try {
      if (store.getItem(key)) return true;
    } catch {
      // Skip a store that throws on read.
    }
  }
  return Boolean(await nativeStateStore()?.get(key));
}

// The store that still has this exact state — localStorage first, then
// the native one for the app, which is the only copy that survived the round
// trip there. Every copy is removed, so the record cannot be replayed after
// success or after a failed exchange.
async function takeState(key) {
  let raw = null;
  for (const store of stateStores()) {
    try {
      const value = store.getItem(key);
      if (value && raw === null) raw = value;
    } catch {
      // Skip a store that throws on read.
    }
  }
  if (raw === null) {
    raw = (await nativeStateStore()?.get(key)) || null;
  }
  await forgetState(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function randomUrlSafeString() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// The registered redirect is the bare app origin — both the production domain
// and the local dev origin are on the app's allow-list at the identity server.
// The hosted logout returns here too, in every host.
function appOrigin() {
  return window.location.origin;
}

// Where a sign-in returns. A host with a native sign-in sheet returns to the
// sheet's own address, which the sheet catches on the device; every other host
// returns to the app origin. The exchange repeats the same value, so the two
// always agree.
function redirectUri() {
  return useSignInSheet()?.redirectUri ?? appOrigin();
}

// The sign-in runs in the sheet and the page stays put, so the key in memory and
// in IndexedDB is the one the code is bound to. What the sheet returns carries
// `?code=&state=` (or `?error=`), and whose answer it is decides the next step:
//
//   - the state is one this app parked: the identity server answered through its own
//     callback, and the query is its one-time code (a deployed server with HTTPS);
//   - any other state is Cognito's: Cognito returned straight to the app, and the app
//     relays the answer to the identity server, which replies with its one-time code and
//     the app's own state (the identity ADR, point 25). That is how a phone signs in against a
//     laptop stack with no HTTPS.
//
// Either way the page then loads itself with `?code=&state=` (or `?error=`) so the one
// boot path completes it, exactly as it completes a browser's return. The load is
// same-origin and never leaves the app. A closed sheet returns null: nothing happens,
// and the person is where they were: the caller is told, by the answer `SIGN_IN_CANCELLED`, so
// it can put something on screen, because the page that started the sign-in may have nothing
// drawn under it.
async function signInThroughSheet(sheet, url) {
  const returned = await sheet.open(url);
  if (!returned) {
    return SIGN_IN_CANCELLED;
  }
  const query = new URL(returned).searchParams;
  const state = query.get('state');
  const isOurAnswer = Boolean(state) && await hasState(`${STATE_KEY_PREFIX}${state}`);
  const landing = isOurAnswer ? query : await relayedQuery(query);
  window.location.replace(`${appOrigin()}/?${landing.toString()}`);
}

// Hands Cognito's answer to the identity server and shapes its reply as the query a
// browser's return would carry. A refused relay lands as an error the sign-in screen can
// name; the parked state record is left for the boot path to clear.
async function relayedQuery(query) {
  const landing = new URLSearchParams();
  let answer;
  try {
    answer = await relayCallback({ code: query.get('code'), state: query.get('state'), error: query.get('error') });
  } catch (refusal) {
    landing.set('error', `RELAY_FAILED_${refusal?.statusCode ?? 'NETWORK'}`);
    return landing;
  }
  if (answer.state) landing.set('state', answer.state);
  if (answer.error) {
    landing.set('error', answer.error);
  } else {
    landing.set('code', answer.code);
  }
  return landing;
}

// What a closed sheet answers. Also a code the retry screen has words for.
export const SIGN_IN_CANCELLED = 'SIGN_IN_CANCELLED';

async function beginAuthorization({ targetPath = '/', mode = 'login', identityProvider, prompt } = {}) {
  const state = randomUrlSafeString();
  const { codeVerifier, codeChallenge } = await createPkcePair();
  const url = await authorizeUrl({
    redirectUri: redirectUri(),
    state,
    codeChallenge,
    identityProvider,
    prompt,
    intent: mode === 'link' ? 'link' : undefined
  });
  await rememberState(`${STATE_KEY_PREFIX}${state}`, { targetPath, mode, codeVerifier });
  const sheet = useSignInSheet();
  if (sheet) {
    return signInThroughSheet(sheet, url);
  }
  window.location.assign(url);
  return undefined;
}

// A first sign-in is a plain login: an active session at the hosted login
// SSO-ing the person straight back in is exactly what we want — it works
// silently only for a key this Human has already registered; for any other key
// the identity server makes the sign-in a hands-on one itself. `prompt: 'login'`
// asks for a hands-on sign-in outright — used by the "connect to an existing
// account" hand-off, where the person must be able to choose a DIFFERENT
// account than the one whose live session is sitting at the provider, and as
// the answer to `login_required` below.
export async function beginLogin(targetPath = '/', { prompt } = {}) {
  return beginAuthorization({ targetPath, prompt });
}

// Link-mode login (account-linking design, "Manual linking"): the owner is
// signed in and authenticates the login to ATTACH. The return trip carries
// mode 'link'; what comes back names that login for the attach call and is
// never a session. `providerName` is the pool's provider name (omitted
// for the local directory's own form).
export async function beginLinkLogin(providerName, targetPath = '/') {
  return beginAuthorization({ targetPath, mode: 'link', identityProvider: providerName, prompt: 'login' });
}

// Completes the round trip when the current load is a sign-in return. Returns
// null when it isn't; otherwise, on success, `{ tokens, targetPath, mode }` for
// a sign-in that found its Human or `{ pendingLogin, targetPath, mode }` for a
// login no account owns yet ('login' is a sign-in, 'link' is an attach round
// trip); or `{ error, mode }` on any failure (cancelled login, state
// mismatch, failed exchange). Callers route errors to the retry screen — never
// back into beginLogin, or a cancelled login would loop forever.
export async function completeLoginIfCallback() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const error = params.get('error');
  if (!code && !error) {
    return null;
  }
  window.history.replaceState({}, '', window.location.pathname);
  const parsedState = await takeState(`${STATE_KEY_PREFIX}${params.get('state')}`);
  const mode = parsedState?.mode || 'login';
  if (error) {
    return answerToRefusedSignIn(error, mode, parsedState?.targetPath);
  }
  if (!parsedState) {
    return { error: 'LOGIN_STATE_MISMATCH', mode };
  }
  let answer;
  try {
    answer = await exchangeCode({ code, redirectUri: redirectUri(), codeVerifier: parsedState.codeVerifier });
  } catch (refusal) {
    return { error: `TOKEN_EXCHANGE_FAILED_${refusal?.statusCode ?? 'NETWORK'}`, mode };
  }
  markFreshLogin(false);
  const targetPath = parsedState.targetPath || '/';
  if (!answer.account_pending) {
    return { tokens: answer, targetPath, mode };
  }
  return {
    pendingLogin: {
      accessToken: answer.access_token,
      id: answer.pending_login,
      outcome: answer.outcome,
      providers: answer.providers || [],
      identity: answer.identity ?? null
    },
    targetPath,
    mode
  };
}

// `login_required` means the hosted login answered silently for a key this
// Human has not registered — on a shared browser, a key another person's
// account holds. The identity server registers a key only on a sign-in it made
// the person do by hand, so: ask for exactly that, once. A second refusal in a
// row is reported instead.
async function answerToRefusedSignIn(error, mode, targetPath = '/') {
  if (error === 'login_required' && !freshLoginUnderWay() && markFreshLogin(true)) {
    await beginLogin(targetPath, { prompt: 'login' });
    return { error: 'SIGNING_IN_AGAIN', mode };
  }
  markFreshLogin(false);
  return { error, mode };
}

// The hosted logout ends the identity provider's own session. Without it, a
// local-only logout followed by the on-load login redirect silently signs the
// user straight back in.
export function hostedLogoutUrl() {
  return logoutUrl(appOrigin());
}

// Consulted by the host's 401 handler: teardown 401s during sign-out must not
// hijack the navigation to the hosted logout.
let signOutInProgress = false;

export function isSigningOut() {
  return signOutInProgress;
}

// Terminal. In a browser: a full-page navigation to the hosted logout, which ends the
// identity provider's own session. In the phone shell the sign-in sheet's browsing session
// is thrown away when the sheet closes, so no hosted session outlives a sign-in and there is
// nothing to end: the page reloads at the app, and the next sign-in is by hand. The web view
// never visits the hosted logout.
export function beginLogout() {
  signOutInProgress = true;
  if (useSignInSheet()) {
    window.location.assign(appOrigin());
    return;
  }
  window.location.assign(hostedLogoutUrl());
}

export const brokeredLogin = { beginLogin, beginLinkLogin, completeLoginIfCallback, hostedLogoutUrl, beginLogout, isSigningOut };
export default brokeredLogin;
